import {
    AlertDialog,
    AlertDialogAction,
    AlertDialogCancel,
    AlertDialogContent,
    AlertDialogDescription,
    AlertDialogFooter,
    AlertDialogHeader,
    AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { Button } from '@/components/ui/button';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { deleteMaDocumentImportLogs, getMaDocumentImportLogs, getZybook, type MaDocumentImportLog } from '@/lib/zybooks-api';
import { Loader2 } from 'lucide-react';
import { useCallback, useState } from 'react';

function formatLogTime(iso: string | null): string {
    if (!iso) {
        return '—';
    }
    const d = new Date(iso);
    if (Number.isNaN(d.getTime())) {
        return iso;
    }
    return d.toLocaleString();
}

export type ImportLogsDialogProps = {
    onOpenChange: (open: boolean) => void;
    defaultZybookCode: string;
};

export function ImportLogsDialog({ onOpenChange, defaultZybookCode }: ImportLogsDialogProps) {
    const [zybookCodeInput, setZybookCodeInput] = useState(defaultZybookCode);
    const [activeZybookCode, setActiveZybookCode] = useState<string | null>(null);
    const [activeZybookId, setActiveZybookId] = useState<number | null>(null);
    const [logs, setLogs] = useState<MaDocumentImportLog[]>([]);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState<string | null>(null);
    const [hasLoaded, setHasLoaded] = useState(false);
    const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
    const [deleteRunning, setDeleteRunning] = useState(false);
    const [deleteError, setDeleteError] = useState<string | null>(null);

    const busy = loading || deleteRunning;

    const fetchMaDocumentImportLogs = useCallback(async (zybookCode: string) => {
        const trimmedZybookCode = zybookCode.trim();
        if (!trimmedZybookCode) {
            setError('Enter a zybook code.');
            return;
        }
        setLoading(true);
        setError(null);
        setDeleteError(null);
        try {
            const zybook = await getZybook(trimmedZybookCode);
            const next = await getMaDocumentImportLogs(zybook.zybook_id);
            setLogs(next);
            setActiveZybookId(zybook.zybook_id);
            setActiveZybookCode(trimmedZybookCode);
            setHasLoaded(true);
        } catch (e) {
            setLogs([]);
            setActiveZybookCode(null);
            setActiveZybookId(null);
            setHasLoaded(false);
            setError((e as Error).message);
        } finally {
            setLoading(false);
        }
    }, []);

    const loadLogs = useCallback(async () => {
        await fetchMaDocumentImportLogs(zybookCodeInput);
    }, [fetchMaDocumentImportLogs, zybookCodeInput]);

    const performDeleteLogs = useCallback(async () => {
        if (activeZybookId == null) {
            return;
        }
        setDeleteDialogOpen(false);
        setDeleteRunning(true);
        setDeleteError(null);
        try {
            await deleteMaDocumentImportLogs(activeZybookId);
            setLogs([]);
        } catch (e) {
            setDeleteError((e as Error).message);
        } finally {
            setDeleteRunning(false);
        }
    }, [activeZybookId]);

    return (
        <>
            <Dialog
                open
                onOpenChange={open => {
                    onOpenChange(open);
                    if (open) {
                        const trimmed = zybookCodeInput.trim();
                        if (trimmed) {
                            void fetchMaDocumentImportLogs(trimmed);
                        }
                    }
                }}
            >
                <DialogContent className="flex max-h-[85vh] max-w-4xl flex-col gap-0 overflow-hidden p-6 sm:max-w-4xl">
                    <DialogHeader className="shrink-0 space-y-1 pr-8">
                        <DialogTitle>Import logs</DialogTitle>
                    </DialogHeader>

                    <div className="border-border mt-4 shrink-0 space-y-3">
                        <label
                            htmlFor="zybook-code-logs-dialog"
                            className="text-sm font-medium"
                        >
                            Zybook code
                        </label>
                        <div className="flex flex-wrap gap-2">
                            <input
                                id="zybook-code-logs-dialog"
                                className="border-input bg-background ring-offset-background placeholder:text-muted-foreground focus-visible:ring-ring flex h-10 min-w-[200px] flex-1 rounded-md border px-3 py-2 text-sm focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-offset-2"
                                placeholder="e.g. ProgrammingInPython2Dev"
                                value={zybookCodeInput}
                                onChange={e => setZybookCodeInput(e.target.value)}
                                disabled={busy}
                                onKeyDown={e => {
                                    if (e.key === 'Enter') {
                                        void loadLogs();
                                    }
                                }}
                            />
                            <Button
                                type="button"
                                onClick={() => void loadLogs()}
                                disabled={busy}
                            >
                                {loading ? (
                                    <>
                                        <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                                        Loading…
                                    </>
                                ) : (
                                    'View logs'
                                )}
                            </Button>
                            <Button
                                type="button"
                                variant="destructive"
                                size="sm"
                                disabled={busy || activeZybookId == null}
                                onClick={() => setDeleteDialogOpen(true)}
                                title={
                                    activeZybookId != null
                                        ? `Delete all import logs stored for ${activeZybookCode ?? `zybook id ${activeZybookId}`}`
                                        : 'Load logs once to enable delete'
                                }
                            >
                                {deleteRunning ? (
                                    <>
                                        <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                                        Deleting…
                                    </>
                                ) : (
                                    'Delete all logs'
                                )}
                            </Button>
                        </div>
                        {error && <p className="text-destructive text-sm">{error}</p>}
                        {deleteError && <p className="text-destructive text-sm">{deleteError}</p>}
                    </div>

                    {hasLoaded && activeZybookId != null && !error && (
                        <div className="mt-4 flex min-h-0 flex-1 flex-col gap-2 overflow-hidden">
                            <p className="text-muted-foreground shrink-0 text-sm">
                                {logs.length === 0
                                    ? `No import logs for ${activeZybookCode}.`
                                    : `${logs.length} section(s) with log entries for ${activeZybookCode}.`}
                            </p>
                            {logs.length > 0 && (
                                <div className="min-h-0 flex-1 overflow-auto rounded-md border">
                                    <Table>
                                        <TableHeader>
                                            <TableRow>
                                                <TableHead className="w-[72px]">Chapter</TableHead>
                                                <TableHead className="w-[72px]">Section</TableHead>
                                                <TableHead>Section GUID</TableHead>
                                                <TableHead>Status</TableHead>
                                                <TableHead className="min-w-[280px] max-w-[480px]">Error</TableHead>
                                                <TableHead>Document name</TableHead>
                                                <TableHead>GDoc ID</TableHead>
                                                <TableHead>Created</TableHead>
                                            </TableRow>
                                        </TableHeader>
                                        <TableBody>
                                            {logs.map(row => (
                                                <TableRow key={row.ma_document_import_log_id ?? `${row.section_guid}-${row.created_at}`}>
                                                    <TableCell className="align-top tabular-nums">{row.chapter_number}</TableCell>
                                                    <TableCell className="align-top tabular-nums">{row.section_number}</TableCell>
                                                    <TableCell className="max-w-[180px] align-top font-mono text-xs">
                                                        {row.section_guid}
                                                    </TableCell>
                                                    <TableCell className="align-top">{row.status}</TableCell>
                                                    <TableCell
                                                        className="min-w-[280px] max-w-[480px] wrap-break-word align-top text-sm"
                                                        title={row.status_message ?? ''}
                                                    >
                                                        {row.status_message}
                                                    </TableCell>
                                                    <TableCell
                                                        className="max-w-[160px] align-top text-sm"
                                                        title={row.document_name ?? ''}
                                                    >
                                                        {row.document_name ?? '—'}
                                                    </TableCell>
                                                    <TableCell
                                                        className="max-w-[160px] align-top font-mono text-xs"
                                                        title={row.gdoc_id ?? ''}
                                                    >
                                                        {row.gdoc_id ? (
                                                            <a
                                                                href={`https://docs.google.com/document/d/${encodeURIComponent(row.gdoc_id)}/edit`}
                                                                target="_blank"
                                                                rel="noopener noreferrer"
                                                                className="text-primary underline-offset-2 hover:underline"
                                                            >
                                                                {row.gdoc_id}
                                                            </a>
                                                        ) : (
                                                            '—'
                                                        )}
                                                    </TableCell>
                                                    <TableCell className="whitespace-nowrap align-top text-sm">
                                                        {row.created_at ? formatLogTime(row.created_at) : '—'}
                                                    </TableCell>
                                                </TableRow>
                                            ))}
                                        </TableBody>
                                    </Table>
                                </div>
                            )}
                        </div>
                    )}
                </DialogContent>
            </Dialog>

            <AlertDialog
                open={deleteDialogOpen}
                onOpenChange={setDeleteDialogOpen}
            >
                <AlertDialogContent>
                    <AlertDialogHeader>
                        <AlertDialogTitle>Delete all import logs?</AlertDialogTitle>
                        <AlertDialogDescription>
                            This removes every MaDocumentImportLogs row for <span className="font-medium">{activeZybookCode ?? ''}</span>.
                            This cannot be undone.
                        </AlertDialogDescription>
                    </AlertDialogHeader>
                    <AlertDialogFooter>
                        <AlertDialogCancel type="button">Cancel</AlertDialogCancel>
                        <AlertDialogAction
                            type="button"
                            className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
                            onClick={() => void performDeleteLogs()}
                        >
                            Delete all
                        </AlertDialogAction>
                    </AlertDialogFooter>
                </AlertDialogContent>
            </AlertDialog>
        </>
    );
}
